import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, Package, Truck, Home, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ICONS = [Sparkles, Package, Truck, Truck, Home];

export default function OrderTracking() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (oid, mail) => {
    const id = (oid || "").trim();
    const em = (mail || "").trim().toLowerCase();
    if (!id || !em) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/track", { order_id: id, email: em });
      setOrder(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order not found");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch when URL contains both params (e.g. coming from checkout success)
  useEffect(() => {
    const id = params.get("id");
    const em = params.get("email");
    if (id && em) lookup(id, em);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    if (!orderId.trim() || !email.trim()) return toast.error("Please fill both fields");
    lookup(orderId, email);
  };

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="track-page">
      <div className="ie-overline text-brand-accent mb-3">Track Order</div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mb-12">Where is my piece?</h1>

      <div className="grid md:grid-cols-12 gap-10">
        <form onSubmit={submit} className="md:col-span-5 space-y-6 md:sticky md:top-28 md:self-start" data-testid="track-form">
          <div>
            <label className="ie-overline text-text-secondary block mb-2">Order ID</label>
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value.toUpperCase())}
              placeholder="IE-XXXXXXXX"
              className="ie-input"
              data-testid="track-order-id"
              required
            />
          </div>
          <div>
            <label className="ie-overline text-text-secondary block mb-2">Email used at checkout</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ie-input"
              data-testid="track-email"
              required
            />
          </div>
          <button type="submit" disabled={loading} className="ie-btn-primary w-full justify-center" data-testid="track-submit">
            {loading ? "Locating…" : "Locate my order"}
          </button>
          <p className="text-[11px] text-text-secondary leading-relaxed">
            Don't have your Order ID? Check your confirmation email or your <a href="/profile" className="underline hover:text-foreground">profile orders</a>.
          </p>
        </form>

        <div className="md:col-span-7">
          <AnimatePresence mode="wait">
            {!order ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="border border-dashed border-border bg-surface p-10 text-center" data-testid="track-empty">
                <div className="ie-overline text-text-secondary mb-3">Awaiting</div>
                <div className="font-serif text-2xl mb-3">Enter your details</div>
                <p className="text-sm text-text-secondary leading-relaxed max-w-md mx-auto">
                  We'll show you the journey of your piece — from atelier preparation to your doorstep.
                </p>
              </motion.div>
            ) : (
              <motion.div key="loaded" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8" data-testid="track-result">
                <div className="border border-border bg-surface p-6 sm:p-8">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                    <div>
                      <div className="ie-overline text-brand-accent">Order</div>
                      <div className="font-serif text-2xl sm:text-3xl">{order.order_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="ie-overline text-text-secondary">Status</div>
                      <div className="ie-overline text-brand-accent">{order.status.replace(/-/g, " ")}</div>
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary mt-3">
                    Placed on {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  <div className="text-sm text-text-secondary">
                    Estimated delivery · <span className="text-foreground font-serif">{new Date(order.estimated_delivery).toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" })}</span>
                  </div>
                  {order.courier_name && (
                    <div className="text-sm text-text-secondary mt-1" data-testid="track-courier-info">
                      Via {order.courier_name}{order.awb_code ? ` · AWB ${order.awb_code}` : ""}
                      {order.tracking_url && (
                        <>
                          {" · "}
                          <a href={order.tracking_url} target="_blank" rel="noreferrer" className="underline hover:text-foreground" data-testid="track-courier-link">
                            Live courier tracking ↗
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Timeline */}
                <div className="border border-border bg-surface p-6 sm:p-8" data-testid="track-timeline">
                  <div className="ie-overline text-brand-accent mb-6">The Journey</div>
                  <ol className="relative space-y-7 pl-7">
                    <span className="absolute left-2.5 top-2 bottom-2 w-px bg-border"/>
                    {order.timeline.map((step, i) => {
                      const Icon = ICONS[i] || Circle;
                      return (
                        <li key={i} className="relative" data-testid={`track-step-${i}`}>
                          <span className={`absolute -left-7 top-0 w-5 h-5 flex items-center justify-center ${step.done ? "text-brand-accent" : "text-text-secondary"}`}>
                            {step.done ? <CheckCircle2 className="w-5 h-5 fill-current text-brand-accent"/> : <Circle className="w-5 h-5"/>}
                          </span>
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="font-serif text-lg sm:text-xl">{step.label}</div>
                            <div className="ie-overline text-text-secondary text-right flex-shrink-0">
                              {step.ts ? new Date(step.ts).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "Pending"}
                            </div>
                          </div>
                          {/* {i === 0 && step.done && (
                            <div className="text-xs text-text-secondary mt-1">A confirmation has been sent to {order.email}.</div>
                          )} */}
                          {i === 0 && step.done && (
                           <div className="text-xs text-text-secondary mt-1">
                                                   An automated call will be made to confirm your order.
                           </div>
                        )}
                        </li>
                      );
                    })}
                  </ol>
                </div>

                {/* Items */}
                <div className="border border-border bg-surface p-6 sm:p-8">
                  <div className="ie-overline text-brand-accent mb-5">Pieces · {order.items.length}</div>
                  <ul className="space-y-4">
                    {order.items.map((it, i) => (
                      <li key={i} className="flex gap-4">
                        <div className="w-16 h-20 bg-background flex-shrink-0 overflow-hidden">
                          {it.image && <img src={it.image} alt="" className="w-full h-full object-cover"/>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-base sm:text-lg truncate">{it.name}</div>
                          <div className="ie-overline text-text-secondary mt-0.5">Size {it.size} · Qty {it.qty}</div>
                        </div>
                        <div className="font-serif text-sm sm:text-base flex-shrink-0">₹{(it.price * it.qty).toLocaleString("en-IN")}</div>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-border mt-5 pt-5 flex justify-between items-baseline">
                    <span className="ie-overline">Total</span>
                    <span className="font-serif text-2xl">₹{order.total.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}